import React, { useEffect, useState } from "react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import {
  FileText, User, Search, Warehouse, Truck, Building2, Clock, CreditCard,
  RefreshCw, CheckCircle2, XCircle, AlertTriangle, TrendingUp, DollarSign,
} from "lucide-react";
import { Skeleton } from "../../components/ui/skeleton";
import { Input } from "../../components/ui/input";
import api from "../../lib/api";
import { money, statusCategory, CATEGORY_DOT } from "../../lib/statuses";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const KPI_DEFS = [
  { key: "total_claims", label: "Total Claims", icon: FileText, color: "text-blue-600 bg-blue-50" },
  { key: "with_customer", label: "With Customer", icon: User, color: "text-amber-600 bg-amber-50" },
  { key: "under_review", label: "Under Review", icon: Search, color: "text-amber-600 bg-amber-50" },
  { key: "in_store", label: "In Store", icon: Warehouse, color: "text-blue-600 bg-blue-50" },
  { key: "in_shipping", label: "In Shipping", icon: Truck, color: "text-blue-600 bg-blue-50" },
  { key: "with_vendor", label: "With Vendor", icon: Building2, color: "text-purple-600 bg-purple-50" },
  { key: "pending_vendor", label: "Pending Vendor", icon: Clock, color: "text-purple-600 bg-purple-50" },
  { key: "credit_approved", label: "Credit Approved", icon: CreditCard, color: "text-emerald-600 bg-emerald-50" },
  { key: "replacement_approved", label: "Replacement Approved", icon: RefreshCw, color: "text-emerald-600 bg-emerald-50" },
  { key: "completed", label: "Completed", icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
  { key: "rejected", label: "Rejected", icon: XCircle, color: "text-rose-600 bg-rose-50" },
  { key: "overdue", label: "Overdue", icon: AlertTriangle, color: "text-rose-600 bg-rose-50" },
];

const PIE_COLORS = ["#2563EB", "#f59e0b", "#9333ea", "#10b981", "#ef4444", "#64748b", "#06b6d4", "#ec4899"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async () => {
    const params = new URLSearchParams();
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    const { data } = await api.get(`/admin/dashboard?${params}`);
    setData(data);
  };

  useEffect(() => { load(); }, [from, to]);

  return (
    <AdminLayout title="Dashboard">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-slate-500">Filter by date:</span>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-auto" data-testid="dash-date-from" />
        <span className="text-slate-400">—</span>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-auto" data-testid="dash-date-to" />
        {(from || to) && <button onClick={() => { setFrom(""); setTo(""); }} className="text-sm text-blue-600 hover:underline">Clear</button>}
      </div>

      {!data ? (
        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {KPI_DEFS.map((k) => (
              <div key={k.key} data-testid={`kpi-${k.key}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${k.color}`}>
                  <k.icon className="h-[18px] w-[18px]" />
                </div>
                <div className="font-display text-2xl font-bold text-slate-900">{data.kpis[k.key]}</div>
                <div className="text-xs font-medium text-slate-500">{k.label}</div>
              </div>
            ))}
          </div>

          {/* financial summary */}
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FinCard icon={DollarSign} label="Claims Value" value={money(data.claims_value)} />
            <FinCard icon={CreditCard} label="Approved Credit Value" value={money(data.approved_credit_value)} />
            <FinCard icon={Building2} label="Outstanding Supplier Credit" value={money(data.outstanding_supplier_credit)} />
            <FinCard icon={Clock} label="Avg Processing Time" value={`${data.avg_processing_days} days`} />
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <ChartCard title="Claims Trend">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Claims by Status">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={data.by_status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.value}>
                    {data.by_status.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Claims by Supplier">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.by_supplier} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip />
                  <Bar dataKey="value" fill="#9333ea" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Claims by Brand">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.by_brand}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#2563EB" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

function FinCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wide">{label}</span></div>
      <div className="mt-2 font-display text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 font-display text-base font-semibold text-slate-900">{title}</h3>
      {children}
    </div>
  );
}
